/**
 * Organization Module Analytics Detail Page
 *
 * Detailed view of a specific training module's performance.
 * Route: /org/:orgSlug/analytics/training/modules/:moduleId
 */

import { Link, useParams } from 'react-router-dom';
import { useGetOrgModuleDetailQuery } from '../store/api/orgAnalyticsApi';
import { useGetOrganizationBySlugQuery } from '../store/api/trainingApi';
import { formatRate } from '../utils/scoreDisplay';

export function OrgModuleAnalyticsDetailPage() {
  const { orgSlug, moduleId } = useParams<{ orgSlug: string; moduleId: string }>();

  // Get org ID from slug
  const { data: org, isLoading: loadingOrg } = useGetOrganizationBySlugQuery(orgSlug || '', {
    skip: !orgSlug,
  });

  const { data: detail, isLoading: loadingDetail } = useGetOrgModuleDetailQuery(
    { orgId: org?.id || '', moduleId: moduleId || '' },
    { skip: !org?.id || !moduleId }
  );

  if (loadingOrg || loadingDetail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!org || !detail) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900">Module not found</h1>
        <Link to={`/org/${orgSlug}/analytics/training`} className="mt-4 text-indigo-600">
          Back to Training Analytics
        </Link>
      </div>
    );
  }

  const totalAssignments =
    detail.assignmentStatusCounts.assigned +
    detail.assignmentStatusCounts.in_progress +
    detail.assignmentStatusCounts.completed +
    detail.assignmentStatusCounts.failed;

  const completionRate =
    totalAssignments > 0 ? detail.assignmentStatusCounts.completed / totalAssignments : null;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to={`/org/${orgSlug}/analytics`} className="text-indigo-600 hover:text-indigo-700">
          Analytics
        </Link>
        <span className="text-gray-400">/</span>
        <Link
          to={`/org/${orgSlug}/analytics/training`}
          className="text-indigo-600 hover:text-indigo-700"
        >
          Training
        </Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-600">{detail.moduleName}</span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{detail.moduleName}</h1>
        <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
          <span>Domain: {detail.domainKey}</span>
          <span>•</span>
          <span>Scenario: {detail.scenarioKey}</span>
          {detail.npcPersonaLabel && (
            <>
              <span>•</span>
              <span>NPC: {detail.npcPersonaLabel}</span>
            </>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Assignments</h3>
          <div className="mt-2 text-3xl font-bold text-gray-900">{totalAssignments}</div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Completion Rate</h3>
          <div className="mt-2 text-3xl font-bold text-green-600">
            {formatRate(completionRate)}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">In Progress</h3>
          <div className="mt-2 text-3xl font-bold text-yellow-600">
            {detail.assignmentStatusCounts.in_progress}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Failed</h3>
          <div className="mt-2 text-3xl font-bold text-red-600">
            {detail.assignmentStatusCounts.failed}
          </div>
        </div>
      </div>

      {/* Score Distribution */}
      {detail.scoreDistribution.overall.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Distribution</h2>
          <div className="grid grid-cols-5 gap-4">
            <ScoreHistogram label="Overall" scores={detail.scoreDistribution.overall} />
            <ScoreHistogram label="Clarity" scores={detail.scoreDistribution.clarity} />
            <ScoreHistogram label="Structure" scores={detail.scoreDistribution.structure} />
            <ScoreHistogram label="Accuracy" scores={detail.scoreDistribution.accuracy} />
            <ScoreHistogram label="Complexity" scores={detail.scoreDistribution.complexity} />
          </div>
        </div>
      )}

      {/* Recent Assignments */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Recent Assignments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignee
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {detail.recentAssignments.map((assignment) => (
                <tr key={assignment.assignmentId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{assignment.assigneeName}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge status={assignment.status} />
                  </td>
                  <td className="px-6 py-4 text-center">
                    {assignment.lastScoreOverall != null ? (
                      <span
                        className={`font-medium ${
                          assignment.lastScoreOverall >= 70
                            ? 'text-green-600'
                            : assignment.lastScoreOverall >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {assignment.lastScoreOverall.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-500">
                    {assignment.lastCompletedAt
                      ? new Date(assignment.lastCompletedAt).toLocaleDateString()
                      : '-'}
                  </td>
                </tr>
              ))}
              {detail.recentAssignments.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No assignments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    assigned: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  const labels: Record<string, string> = {
    assigned: 'Assigned',
    in_progress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.assigned}`}>
      {labels[status] || status}
    </span>
  );
}

function ScoreHistogram({ label, scores }: { label: string; scores: number[] }) {
  if (scores.length === 0) {
    return (
      <div className="text-center">
        <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
        <div className="h-24 flex items-center justify-center text-gray-400 text-sm">
          No data
        </div>
      </div>
    );
  }

  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const min = Math.min(...scores);
  const max = Math.max(...scores);

  // Create simple bins: 0-50, 50-70, 70-85, 85-100
  const bins = [0, 0, 0, 0];
  for (const score of scores) {
    if (score < 50) bins[0]++;
    else if (score < 70) bins[1]++;
    else if (score < 85) bins[2]++;
    else bins[3]++;
  }

  const maxBin = Math.max(...bins);

  return (
    <div className="text-center">
      <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
      <div className="h-24 flex items-end justify-center gap-1">
        {bins.map((count, i) => (
          <div
            key={i}
            className={`w-6 rounded-t ${
              i === 0 ? 'bg-red-300' : i === 1 ? 'bg-yellow-300' : i === 2 ? 'bg-green-300' : 'bg-green-500'
            }`}
            style={{ height: `${maxBin > 0 ? (count / maxBin) * 100 : 0}%`, minHeight: count > 0 ? '4px' : '0' }}
            title={`${count} scores`}
          />
        ))}
      </div>
      <div className="mt-2 text-xs text-gray-500">
        Avg: {avg.toFixed(1)} ({min.toFixed(0)}-{max.toFixed(0)})
      </div>
    </div>
  );
}

export default OrgModuleAnalyticsDetailPage;
